// Maintenance gate. When VITE_MAINTENANCE === 'true', everyone sees a
// "down for maintenance" screen EXCEPT allowlisted usernames (the team), who
// can sign in and use the app normally. Flip the env var back to disable.
import React, { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import AuthComponent from './Auth';

const MAINTENANCE = import.meta.env.VITE_MAINTENANCE === 'true';
// Usernames allowed through during maintenance (case-insensitive).
const ALLOWLIST = ['aquariusmaximus', 'ajoony'];
// Emails allowed through instantly (no profile query needed).
const ALLOW_EMAILS = ['tavonia@gmail.com'];

type State = 'checking' | 'allowed' | 'blocked';

const MaintenanceScreen: React.FC = () => {
  const [showAuth, setShowAuth] = useState(false);
  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      background: 'radial-gradient(1200px 600px at 50% -10%, rgba(0,194,179,0.10), transparent), #060e18',
      color: 'rgba(234,246,248,0.9)', fontFamily: 'Outfit, sans-serif', padding: 24,
    }}>
      <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 6 }}>
        Gathr<span style={{ color: '#00c2b3' }}>XR</span>
      </div>
      <div style={{ fontSize: 46, marginBottom: 18 }}>🛠️</div>
      <h1 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-1px', margin: '0 0 14px' }}>
        Down for maintenance
      </h1>
      <p style={{ fontSize: 16, lineHeight: 1.6, color: 'rgba(234,246,248,0.6)', maxWidth: 460, margin: 0 }}>
        We're making GathrXR better and will be back shortly. Thanks for your
        patience — check back soon.
      </p>
      <button
        onClick={() => setShowAuth(true)}
        style={{
          marginTop: 30, background: 'none', border: 'none', cursor: 'pointer',
          color: 'rgba(234,246,248,0.28)', fontSize: 12.5, fontFamily: 'Outfit, sans-serif',
          textDecoration: 'underline', textUnderlineOffset: 3,
        }}
      >
        Team sign in
      </button>

      {showAuth && (
        <div
          onClick={() => setShowAuth(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 100, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(3,8,15,0.8)', backdropFilter: 'blur(6px)', padding: 16,
          }}
        >
          <div onClick={(e) => e.stopPropagation()}>
            {/* On success the auth listener re-checks and lets allowed users through */}
            <AuthComponent onSuccess={() => setShowAuth(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

const MaintenanceGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<State>(MAINTENANCE ? 'checking' : 'allowed');

  useEffect(() => {
    if (!MAINTENANCE) return;
    let cancelled = false;

    const evaluate = async () => {
      try {
        // getSession reads the local session (no network round-trip), so it
        // can never hang the way getUser() can when the token endpoint stalls.
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) { if (!cancelled) setState('blocked'); return; }
        const email = (user.email || '').trim().toLowerCase();
        if (ALLOW_EMAILS.includes(email)) { if (!cancelled) setState('allowed'); return; }
        // Username check, but never let a slow query hang the gate.
        const query = supabase.from('profiles').select('username').eq('id', user.id).single();
        const timeout = new Promise<{ data: any }>((res) => setTimeout(() => res({ data: null }), 3000));
        const { data } = await Promise.race([query, timeout]);
        const uname = (data?.username || '').trim().toLowerCase();
        if (!cancelled) setState(ALLOWLIST.includes(uname) ? 'allowed' : 'blocked');
      } catch {
        if (!cancelled) setState('blocked'); // fail safe — never stuck on "Loading…"
      }
    };

    // Absolute backstop: never sit on the loading screen for more than 5s.
    const hardTimeout = setTimeout(() => { if (!cancelled) setState((s) => (s === 'checking' ? 'blocked' : s)); }, 5000);
    evaluate();
    // Defer: supabase-js awaits these callbacks while holding its auth lock, so
    // calling getSession() inside one (directly) deadlocks every later auth call —
    // that froze /welcome after Google sign-in.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => { setTimeout(() => { void evaluate(); }, 0); });
    return () => { cancelled = true; clearTimeout(hardTimeout); subscription.unsubscribe(); };
  }, []);

  if (!MAINTENANCE || state === 'allowed') return <>{children}</>;
  if (state === 'checking') {
    return (
      <div style={{
        minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#060e18', color: 'rgba(234,246,248,0.5)', fontFamily: 'Outfit, sans-serif', fontSize: 14,
      }}>
        Loading…
      </div>
    );
  }
  return <MaintenanceScreen />;
};

export default MaintenanceGate;
